// src/ryan/components/product-blocks/TutoriaDemoBlock.tsx
// Bloco de demonstração "Tutoria" — pra produtos de apostila de tutoria
// médica, cujo diferencial é organizar o conteúdo na ordem lógica em que
// ele é discutido em grupo (epidemiologia → etiologia → fatores de risco →
// fisiopatologia → manifestações clínicas → diagnóstico → tratamento →
// prevenção).
//
// COREOGRAFIA (ver useEffect mais abaixo): os cartões nascem embaralhados
// (ordem determinística via hash do id, não Math.random — não mais
// coordenadas left/top calculadas em px) e, depois de um silêncio inicial,
// se organizam um de cada vez, na ordem lógica. Cada cartão usa a prop
// `layout` do Framer Motion (não `layoutId` — é o MESMO elemento mudando
// de posição, não dois elementos se fundindo) pra animar suavemente a
// troca de estado — essa é a origem da animação fluida aprovada.
//
// POSICIONAMENTO: os cards NÃO usam coordenadas left/top calculadas por
// fórmula fixa em px — .rpb-tut-canvas é um flex container comum
// (`flex-wrap: wrap`): o navegador decide quantos cards cabem por linha de
// acordo com a largura REAL do bloco, e a altura cresce naturalmente com o
// conteúdo — sem min-height calculado em JS, sem breakpoint manual, sem
// overflow em nenhum eixo (aprendizado direto de uma rodada anterior, onde
// coordenadas fixas descoladas do tamanho real do bloco causavam scroll).
// A troca de estado (embaralhado ↔ organizado) continua sendo a prop
// `layout` do Framer Motion: ela reanima suavemente a MUDANÇA DE ORDEM dos
// elementos no flex (é exatamente pra isso que `layout` serve — FLIP
// automático inclusive quando a ordem no DOM muda), não uma mudança de
// left/top manual.
//
// A linha conectora depende só de CSS (pseudo-elemento `.rpb-tut-card--arrow`,
// ver TutoriaDemoBlock.css), sem nenhuma medição em JS. Mais simples e
// nunca pode ficar geometricamente inconsistente com o layout real.
//
// SEM CARDS DECORATIVOS (reforma de uma rodada anterior): uma tentativa
// introduziu um segundo grupo de cards "decorativos" (fora da sequência
// lógica, girando numa órbita elíptica ao redor do grupo principal) — não
// convenceu visualmente depois de várias iterações e foi abandonada.
//
// VISIBILIDADE (reforma desta rodada): `sequenceIndex` deixou de ser "faz
// parte da sequência vs. decorativo, mas ainda aparece" — agora é
// literalmente "aparece no bloco ou não". Um card com `sequenceIndex: null`
// (checkbox "Faz parte da sequência" desmarcado no admin) NUNCA é
// renderizado aqui — só os marcados aparecem, embaralhados no início e se
// organizando na ordem numérica marcada.

import React, { useState, useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { DemoHeader, resolvePillarIcon } from './DemoBlockShared';
import './TutoriaDemoBlock.css';

// ── Tipos ──────────────────────────────────────────────────────────────

export interface TutoriaCard {
    id: string;
    title: string;
    icon: string;
    // Cor pastel em hex — usada de forma sutil (borda do card, tingimento
    // do fundo e anel do círculo do ícone), nunca como cor dominante do
    // card em si.
    color: string;
    // Controla se o cartão APARECE no bloco — null = card cadastrado no
    // admin mas não marcado ("Faz parte da sequência"), nunca renderizado
    // aqui. Um número = aparece, na ordem lógica dada por esse número (ver
    // RyanAdminProducts.tsx, renumerado automaticamente pela posição na
    // lista do editor, nunca editável diretamente).
    sequenceIndex: number | null;
}

export interface TutoriaDemoData {
    label: string;
    headline: string;
    subheadline: string;
    cards: TutoriaCard[];
    closingLead: string;
    closingEmphasis: string;
}

export const defaultTutoriaDemoData = (): TutoriaDemoData => ({
    label: 'Prepare-se melhor',
    headline: 'Entre na tutoria sabendo exatamente para onde a discussão vai.',
    subheadline: 'Cada apostila organiza o conteúdo na sequência em que ele naturalmente faz sentido, facilitando o aprendizado e a participação durante as tutorias.',
    cards: [
        // ── Sequência principal, ordem lógica de discussão em tutoria ──
        { id: 'tut-1', title: 'Epidemiologia', icon: 'Activity', color: '#FFE4D6', sequenceIndex: 0 },
        { id: 'tut-2', title: 'Etiologia', icon: 'Dna', color: '#E0F2FE', sequenceIndex: 1 },
        { id: 'tut-3', title: 'Fatores de risco', icon: 'Siren', color: '#FEE2E2', sequenceIndex: 2 },
        { id: 'tut-4', title: 'Fisiopatologia', icon: 'Microscope', color: '#EDE9FE', sequenceIndex: 3 },
        { id: 'tut-5', title: 'Manifestações clínicas', icon: 'Stethoscope', color: '#FCE7F3', sequenceIndex: 4 },
        { id: 'tut-6', title: 'Diagnóstico', icon: 'Scan', color: '#DCFCE7', sequenceIndex: 5 },
        { id: 'tut-7', title: 'Tratamento', icon: 'Pill', color: '#CFFAFE', sequenceIndex: 6 },
        { id: 'tut-8', title: 'Prevenção', icon: 'Cross', color: '#FEF9C3', sequenceIndex: 7 },
    ],
    closingLead: 'Toda tutoria segue uma linha de raciocínio.',
    closingEmphasis: 'A organização muda o jeito de estudar.',
});

// ── Helpers determinísticos (nunca Math.random) ──────────────────────

// Hash simples (polinomial) + um finalizador de mistura de bits (mesma
// ideia do finalizador do MurmurHash3 — fmix32). O finalizador é
// necessário: SEM ele, ids que só diferem no último caractere (ex:
// "tut-1", "tut-2", ..., "tut-8" — o padrão real usado neste bloco) geram
// hashes quase lineares entre si (a soma polinomial só muda por +1 a cada
// id consecutivo), e ORDENAR por esse hash reproduzia a ordem original
// quase exatamente — o bug real por trás do "embaralhado" não parecer
// embaralhado (os cards da sequência apareciam praticamente na posição
// final desde o início). O finalizador espalha essa diferença de 1 bit
// por todo o número, então ids parecidos produzem saídas bem diferentes.
function seedFromId(id: string): number {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    h ^= h >>> 16;
    h = Math.imul(h, 0x85ebca6b);
    h ^= h >>> 13;
    h = Math.imul(h, 0xc2b2ae35);
    h ^= h >>> 16;
    return h >>> 0;
}

// Ordem "embaralhada" da Fase 0 — determinística (hash do id), não a
// ordem lógica 1-8 (senão o mural não lê como "desorganizado" desde o
// início). Decide a ORDEM de exibição no flex (e portanto a posição real
// na grade) enquanto o card não é revelado — quando revelado, o card sai
// dessa posição embaralhada e vai pro início da fila, na ordem lógica (ver
// `displayCards`), o que a prop `layout` do Framer Motion anima como um
// deslocamento real na tela — não é mais posição em px calculada
// manualmente, é o próprio flex-wrap decidindo os dois lugares (o
// embaralhado e o final).
function shuffleDeterministic<T extends { id: string }>(cards: T[]): T[] {
    return [...cards].sort((a, b) => seedFromId(a.id) - seedFromId(b.id));
}

// Corrige "pontos fixos" que sobram do hash acima: um card cujo hash o
// deixa na MESMA posição (mesmo índice) que ele ocupa na ordem lógica
// final não se desloca nada ao ser revelado — só perde a rotação leve
// (deterministicJitterRotate), o que lê como "só estava torto", não como
// "foi reorganizado". Troca cada ponto fixo com o próximo da fila,
// repetindo em passes até não sobrar nenhum (determinístico, sem
// Math.random — mesmo espírito do resto do arquivo).
function derangeScatter<T extends { id: string }>(scatterOrder: T[], logicalOrder: T[]): T[] {
    if (scatterOrder.length < 2) return scatterOrder;
    const result = [...scatterOrder];
    for (let pass = 0; pass < result.length; pass++) {
        let fixedPointFound = false;
        for (let i = 0; i < result.length; i++) {
            if (result[i].id === logicalOrder[i].id) {
                fixedPointFound = true;
                const j = (i + 1) % result.length;
                [result[i], result[j]] = [result[j], result[i]];
            }
        }
        if (!fixedPointFound) break;
    }
    return result;
}

// Rotação leve pro efeito "mural" no estado embaralhado — sutil de
// propósito (o que comunica "embaralhado" é o card estar numa posição
// diferente da final, não um ângulo forte); some (volta a 0°) assim que o
// card é revelado.
function deterministicJitterRotate(id: string): number {
    const frac = (seedFromId(id + '-rot') % 1000) / 1000;
    return (frac - 0.5) * 6; // -3° a 3°
}

// Tingimento sutil do fundo/anel do card a partir da cor pastel própria —
// hex malformado (dado externo, vindo do admin/backend) nunca deveria
// lançar exceção aqui, só cair no branco neutro.
function hexToRgba(hex: string, alpha: number): string {
    const clean = (hex || '').replace('#', '').trim();
    const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
    const r = parseInt(full.substring(0, 2), 16);
    const g = parseInt(full.substring(2, 4), 16);
    const b = parseInt(full.substring(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return `rgba(255, 255, 255, ${alpha})`;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ── Componente ───────────────────────────────────────────────────────

const TutoriaDemoBlock: React.FC<{ data: TutoriaDemoData }> = ({ data }) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    // Quantos cards já estão revelados na ordem lógica (0..N) — o card de
    // índice k (posição no array) está revelado assim que organizedCount > k.
    const [organizedCount, setOrganizedCount] = useState(0);
    const [closingVisible, setClosingVisible] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [cycleKey, setCycleKey] = useState(0);

    const shouldReduceMotion = useReducedMotion();

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.12 }
        );
        if (wrapRef.current) observer.observe(wrapRef.current);
        return () => observer.disconnect();
    }, []);

    // Guarda defensiva — causa raiz de uma versão anterior do bloco
    // renderizando vazio: `data.cards` chegando como `undefined`/`null`
    // (dado salvo antes deste campo existir, ou forma malformada vinda do
    // backend) fazia `cards.filter` lançar TypeError na primeira linha de
    // render, sem nenhum log visível. Array.isArray garante que `cards`
    // NUNCA é outra coisa daqui pra frente.
    //
    // Só cards com `sequenceIndex` marcado (checkbox "Faz parte da
    // sequência" no admin) aparecem no bloco — os demais nem chegam a
    // renderizar (não é mais "decorativo, mas ainda visível", é "visível
    // ou não"). Ordenados pelo próprio `sequenceIndex` (o admin já mantém
    // esse número sincronizado com a posição escolhida na lista — ver
    // RyanAdminProducts.tsx), não pela ordem crua de `data.cards`.
    const allCards = Array.isArray(data.cards) ? data.cards : [];
    const cards = allCards
        .filter(c => c.sequenceIndex !== null && Number.isFinite(c.sequenceIndex) && (c.sequenceIndex as number) >= 0)
        .sort((a, b) => (a.sequenceIndex as number) - (b.sequenceIndex as number));
    // Ordem embaralhada (Fase 0/enquanto não revelado) — determinística, e
    // sem nenhum card nascendo na própria posição final (ver derangeScatter).
    const scatterOrder = derangeScatter(shuffleDeterministic(cards), cards);

    const resetPhases = () => {
        setOrganizedCount(0);
        setClosingVisible(false);
        setResetting(false);
    };

    useEffect(() => {
        if (!visible) { resetPhases(); return; }

        // Sem cards: nada pra organizar.
        if (cards.length === 0) {
            setClosingVisible(true);
            return;
        }

        // prefers-reduced-motion: pula direto pro estado final estático —
        // sequência revelada, frase visível, sem cascata/loop.
        if (shouldReduceMotion) {
            setOrganizedCount(cards.length);
            setClosingVisible(true);
            return;
        }

        resetPhases();
        const timers: ReturnType<typeof setTimeout>[] = [];

        // ── FASE 0 — silêncio (~2s). Estado embaralhado, nada muda. ──
        let cursor = 2000;

        // ── FASE 1 — revelação sequencial, um cartão por vez, na ordem
        // lógica (posição no array). ──
        const CARD_MOVE_MS = 700; // 600-800ms, ease-in-out, sem bounce
        const CARD_GAP_MS = 250; // pausa entre um assentar e o próximo começar

        cards.forEach((_, idx) => {
            timers.push(setTimeout(() => setOrganizedCount(idx + 1), cursor));
            cursor += CARD_MOVE_MS + CARD_GAP_MS;
        });
        cursor -= CARD_GAP_MS; // a pausa pós-último card vira a pausa própria da Fase 2

        // ── FASE 2 — pequena pausa de respiro depois do último card
        // assentar. Os conectores CSS (.rpb-tut-card::after) NÃO precisam
        // de reserva de tempo aqui: o delay de cada um (0.7s) conta a
        // partir do momento em que O PRÓPRIO card é revelado — que pra
        // todo card exceto o último acontece BEM antes do fim da Fase 1,
        // não no fim dela. O penúltimo conector (o último a existir; o
        // card final não tem seta) já termina de aparecer enquanto o
        // último card ainda está se movendo. Somar a duração total do fade
        // do conector (1050ms) DEPOIS do fim da Fase 1, como antes, era
        // tempo morto — a frase de fechamento demorava quase 2s a mais do
        // que o necessário pra aparecer. ──
        cursor += 350;

        // ── FASE 3 — frase de fechamento, só agora (não mais o fade
        // genérico de scroll: ela é a conclusão da coreografia). ──
        timers.push(setTimeout(() => setClosingVisible(true), cursor));
        cursor += 700; // duração da própria entrada da frase (rpb-tut-closing-in)

        // ── FASE 4 — resultado final parado, tempo de sobra pra ler. Subiu
        // de 3000 pra 6000ms — o ciclo estava reiniciando cedo demais, sem
        // dar tempo de ler a frase de fechamento (que só aparece na Fase 3,
        // logo antes desta pausa) com calma. ──
        cursor += 6000;

        // ── FASE 5 — reset: fade geral, volta tudo ao embaralhado, reinicia. ──
        timers.push(setTimeout(() => setResetting(true), cursor));
        cursor += 550;
        timers.push(setTimeout(() => setCycleKey(k => k + 1), cursor));

        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, data, cycleKey, shouldReduceMotion]);

    const cardTransition = {
        duration: shouldReduceMotion ? 0 : 0.7,
        ease: [0.4, 0, 0.2, 1] as const,
    };

    // Cards já revelados (ordem lógica) vêm primeiro no flex; o resto
    // (ainda não revelados) mantém a ordem embaralhada, só removendo dali
    // quem acabou de ser revelado — evita reembaralhar cards que não
    // mudaram de estado.
    const revealedCards = cards.filter((_, idx) => organizedCount > idx);
    const restCards = scatterOrder.filter(c => !revealedCards.includes(c));
    const displayCards = [...revealedCards, ...restCards];

    return (
        <div className="rpb-demo-wrap" ref={wrapRef}>
            <DemoHeader label={data.label} headline={data.headline} subheadline={data.subheadline} visible={visible} />

            <div className={`rpb-tut-scene ${visible ? 'rpb-tut-scene--visible' : ''}`}>
                <div className={`rpb-tut-dynamic ${resetting ? 'rpb-tut-dynamic--resetting' : ''}`}>
                    {/* Canvas — flex-wrap comum, sem moldura de app/janela como os
                        outros blocos usam (a metáfora aqui é espaço de composição
                        livre, não uma interface simulada). O navegador decide
                        quantos cards cabem por linha e a altura acompanha o
                        conteúdo naturalmente — nunca overflow em nenhum eixo. A
                        troca de estado (embaralhado ↔ revelado) é só uma mudança de
                        ORDEM no array abaixo; quem anima essa mudança suavemente é
                        a prop `layout` do Framer Motion em cada card. */}
                    <div className="rpb-tut-canvas">
                        {displayCards.map((card, i) => {
                            const originalIndex = cards.indexOf(card);
                            const isRevealed = organizedCount > originalIndex;
                            const hasNext = originalIndex < cards.length - 1;
                            const rotateDeg = isRevealed ? 0 : deterministicJitterRotate(card.id);
                            const Icon = resolvePillarIcon(card.icon);

                            return (
                                <motion.div
                                    key={card.id ?? i}
                                    layout
                                    transition={cardTransition}
                                    animate={{ rotate: rotateDeg, opacity: 1 }}
                                    whileHover={{ y: -4, transition: { duration: 0.18 } }}
                                    className={[
                                        'rpb-tut-card',
                                        isRevealed && hasNext ? 'rpb-tut-card--arrow' : '',
                                    ].filter(Boolean).join(' ')}
                                    style={{
                                        borderColor: card.color,
                                        '--tut-tint': hexToRgba(card.color, 0.12),
                                        '--tut-ring': hexToRgba(card.color, 0.28),
                                    } as React.CSSProperties}
                                >
                                    <div className="rpb-tut-card-icon" style={{ background: card.color }}>
                                        <Icon size={16} />
                                    </div>
                                    <span className="rpb-tut-card-title">{card.title}</span>
                                </motion.div>
                            );
                        })}
                    </div>

                    {(data.closingLead || data.closingEmphasis) && (
                        <p className={`rpb-tut-closing ${closingVisible ? 'rpb-tut-closing--visible' : ''}`}>
                            {data.closingLead}
                            {data.closingLead && data.closingEmphasis ? ' ' : ''}
                            {data.closingEmphasis && <strong>{data.closingEmphasis}</strong>}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TutoriaDemoBlock;
